import React, { useState } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Archive, Pencil, Plus } from 'lucide-react'
import { toast } from 'react-toastify'
import { ConfirmArchiveDialog } from '../components/ConfirmArchiveDialog'
import { archiveIconSx } from '../styles'
import { AdminBrandInput, BrandFormDialog } from './BrandForm'
import { AccountBrandsPanelQuery } from './__generated__/AccountBrandsPanelQuery.graphql'
import { AccountBrandsPanelArchiveMutation } from './__generated__/AccountBrandsPanelArchiveMutation.graphql'

const brandsQuery = graphql`
  query AccountBrandsPanelQuery($input: AdminListBrandsRequest!) {
    adminListBrands(input: $input) {
      data {
        id
        name
        metaAdAccountExternalId
        featureFlagIds
        currencyType
        fbAccessTokenType
        dataGranularity
        minAdSpend
        insightsThreshold
      }
    }
  }
`

const archiveMutation = graphql`
  mutation AccountBrandsPanelArchiveMutation($input: DeleteRequest!) {
    archiveBrand(input: $input) {
      id
    }
  }
`

const granularityLabel = (value: string | null) =>
  value === 'WEEKLY' ? 'Weekly' : value === 'MONTHLY' ? 'Monthly' : '—'

const COLSPAN = 8

interface AccountBrandsPanelProps {
  accountId: string
  accountName: string
  accountFeatureFlagIds: string[]
  onChanged: () => void
}

/**
 * Inline management of an account's brands — the DataGridPro detail panel for an
 * account row. A nested table; add/edit open a dialog, archive is inline.
 */
export const AccountBrandsPanel = ({
  accountId,
  accountName,
  accountFeatureFlagIds,
  onChanged,
}: AccountBrandsPanelProps) => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [formOpen, setFormOpen] = useState(false)
  const [formBrand, setFormBrand] = useState<AdminBrandInput | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const data = useLazyLoadQuery<AccountBrandsPanelQuery>(
    brandsQuery,
    { input: { accountId, pageNumber: 0, pageSize: 200 } },
    { fetchPolicy: 'network-only', fetchKey: String(refreshKey) }
  )
  const brands = data.adminListBrands.data ?? []

  const [archiveBrand, archiving] =
    useMutation<AccountBrandsPanelArchiveMutation>(archiveMutation)

  const refresh = () => {
    setRefreshKey((key) => key + 1)
    onChanged()
  }

  const handleArchive = () => {
    if (!archiveTarget) return
    archiveBrand({
      variables: { input: { id: archiveTarget.id } },
      onCompleted: () => {
        toast.success('Brand archived.')
        setArchiveTarget(null)
        refresh()
      },
      onError: (error) => {
        console.error(error)
        toast.error(error.message || 'Unable to archive brand.')
      },
    })
  }

  const toInput = (brand: (typeof brands)[number]): AdminBrandInput => ({
    id: brand.id,
    name: brand.name,
    metaAdAccountExternalId: brand.metaAdAccountExternalId ?? null,
    featureFlagIds: [...brand.featureFlagIds],
    currencyType: brand.currencyType ?? null,
    fbAccessTokenType: brand.fbAccessTokenType ?? null,
    dataGranularity: brand.dataGranularity ?? null,
    minAdSpend: brand.minAdSpend ?? null,
    insightsThreshold: brand.insightsThreshold ?? null,
  })

  const openCreate = () => {
    setFormBrand(null)
    setFormOpen(true)
  }
  const openEdit = (brand: (typeof brands)[number]) => {
    setFormBrand(toInput(brand))
    setFormOpen(true)
  }

  // Indent the panel so it lines up with the account Name column (past the
  // detail-panel toggle column).
  return (
    <div className="bg-gray-50 py-3 pr-6" style={{ paddingLeft: 52 }}>
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
        Brands
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Brand</TableCell>
            <TableCell>Meta Ad Account</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>Granularity</TableCell>
            <TableCell align="right">Min Ad Spend</TableCell>
            <TableCell align="right">Insights Threshold</TableCell>
            <TableCell align="right">Flags</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {brands.map((brand) => (
            <TableRow key={brand.id} hover>
              <TableCell>{brand.name}</TableCell>
              <TableCell>{brand.metaAdAccountExternalId ?? '—'}</TableCell>
              <TableCell>{brand.currencyType ?? 'USD'}</TableCell>
              <TableCell>{granularityLabel(brand.dataGranularity)}</TableCell>
              <TableCell align="right">{brand.minAdSpend ?? '—'}</TableCell>
              <TableCell align="right">
                {brand.insightsThreshold ?? '—'}
              </TableCell>
              <TableCell align="right">{brand.featureFlagIds.length}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEdit(brand)}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Archive">
                  <IconButton
                    size="small"
                    sx={archiveIconSx}
                    onClick={() =>
                      setArchiveTarget({ id: brand.id, name: brand.name })
                    }
                  >
                    <Archive size={16} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}

          {brands.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLSPAN}>
                <Typography variant="body2" color="text.disabled">
                  No active brands.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="mt-3">
        <Button
          size="small"
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={openCreate}
        >
          Add brand
        </Button>
      </div>

      {formOpen && (
        <BrandFormDialog
          accountId={accountId}
          accountName={accountName}
          brand={formBrand ?? undefined}
          excludeIds={accountFeatureFlagIds}
          onSaved={() => {
            setFormOpen(false)
            refresh()
          }}
          onClose={() => setFormOpen(false)}
        />
      )}

      <ConfirmArchiveDialog
        open={archiveTarget != null}
        title={`Archive '${archiveTarget?.name}'?`}
        message={`Archiving this brand will hide it and all of its records from the app, and its data will no longer be viewable. The data is retained in the database and can be restored later.`}
        loading={archiving}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
      />
    </div>
  )
}

export default AccountBrandsPanel
